// src/components/CustomReportsPage.js
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db, storage } from '../firebaseConfig';
import {
  collection, doc, getDoc, getDocs, query, where,
  setDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import RichTextEditor from './RichTextEditor';
import PaginatedPreview from './PaginatedPreview';
import generateFullReportPdf from './generateFullReportPdf';
import ImageSelectorModal from './ImageSelectorModal';

const fixedSections = [
  'Executive Summary',
  'Observations and Analysis',
  'Recommendations',
  'Conclusion',
];

function CustomReportsPage() {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [role, setRole] = useState('engineer');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [coverData, setCoverData] = useState({});
  const [sectionData, setSectionData] = useState({});
  const [customSections, setCustomSections] = useState([]);
  const [activeSection, setActiveSection] = useState('Cover Page');
  const [viewMode, setViewMode] = useState('edit');
  const [companyLogoUrl, setCompanyLogoUrl] = useState('/VilanoLogo.PNG');
  const [footerHeightPt, setFooterHeightPt] = useState(54);
  const [pdfFont, setPdfFont] = useState('helvetica');
  const [uploading, setUploading] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const debounceTimers = useRef({});
  const editorRef = useRef();

  useEffect(() => {
    const handleOpenPicker = () => setShowImagePicker(true);
    window.addEventListener('openProjectImagePicker', handleOpenPicker);
    return () => window.removeEventListener('openProjectImagePicker', handleOpenPicker);
  }, []);

  useEffect(() => {
    if (!user) return;
    const fetchInitial = async () => {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      let data = {};
      let q;
      if (userDoc.exists()) {
        data = userDoc.data();
        setRole(data.role || 'engineer');
        if (data.companyLogoUrl) setCompanyLogoUrl(data.companyLogoUrl);
        if (data.footerHeightPt) setFooterHeightPt(data.footerHeightPt);
      }
      q =
        data.role === 'admin'
          ? query(collection(db, 'projects'))
          : query(collection(db, 'projects'), where('members', 'array-contains', user.uid));
      const snapshot = await getDocs(q);
      const options = snapshot.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().name,
      }));
      setProjects(options);
    };
    fetchInitial();
  }, [user]);

  useEffect(() => {
    if (!selectedProject) return;
    const load = async () => {
      const { id } = selectedProject;
      const metadataSnap = await getDoc(doc(db, 'projects', id, 'metadata', 'report'));
      const customOrder = metadataSnap.exists() ? metadataSnap.data().customOrder || [] : [];
      setCustomSections(customOrder);

      const coverRef = doc(db, 'projects', id, 'reportSections', 'cover');
      const snap = await getDoc(coverRef);
      if (snap.exists()) {
        setCoverData((prev) => ({ ...prev, ...snap.data() }));
      }

      const q = query(collection(db, 'projects', id, 'reportSections'));
      const docsSnap = await getDocs(q);
      const sections = {};
      docsSnap.forEach((doc) => {
        const name = doc.id === 'cover' ? 'Cover Page' : doc.id;
        if (doc.id !== 'cover') sections[name] = doc.data().content || '';
      });
      setSectionData(sections);
    };
    load();
  }, [selectedProject]);

  const updateCoverField = (field, value) => {
    setCoverData((prev) => ({ ...prev, [field]: value }));
    if (selectedProject) {
      clearTimeout(debounceTimers.current[field]);
      debounceTimers.current[field] = setTimeout(() => {
        setDoc(
          doc(db, 'projects', selectedProject.id, 'reportSections', 'cover'),
          { [field]: value },
          { merge: true }
        );
      }, 500);
    }
  };

  const handleImageUpload = async (e) => {
    if (!selectedProject || !e.target.files[0]) return;
    setUploading(true);
    const file = e.target.files[0];
    const path = `projects/${selectedProject.id}/coverImages/${file.name}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    const refDoc = doc(db, 'projects', selectedProject.id, 'reportSections', 'cover');
    await setDoc(refDoc, { coverImageUrl: url }, { merge: true });
    setCoverData((prev) => ({ ...prev, coverImageUrl: url }));
    setUploading(false);
  };

  return (
    <>
      <div className="custom-reports-header">
        <div className="custom-reports-toggle-group" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={() => setViewMode('edit')}
            className={`custom-reports-toggle-button ${viewMode === 'edit' ? 'active' : ''}`}
          >
            Edit
          </button>
          <button
            onClick={() => {
              if (editorRef.current && activeSection !== 'Cover Page') {
                editorRef.current.triggerPreview();
              } else {
                setViewMode('preview');
              }
            }}
            className={`custom-reports-toggle-button ${viewMode === 'preview' ? 'active' : ''}`}
          >
            Preview
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label htmlFor="projectSelect" style={{ color: 'white', fontSize: '0.9rem' }}>Select Project:</label>
            <select
              id="projectSelect"
              value={selectedProject?.id || ''}
              onChange={(e) => {
                const proj = projects.find(p => p.id === e.target.value);
                setSelectedProject(proj || null);
                setActiveSection('Cover Page');
              }}
              style={{ padding: '0.35rem', borderRadius: '4px', fontSize: '0.9rem' }}
            >
              <option value="" disabled>Select a project</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="custom-reports-page-wrapper">
        <div className="custom-reports-sidebar" style={{ display: 'flex', flexDirection: 'column' }}>
          <img src="/VilanoLogo.png" alt="Logo" className="customSidebar-logo" />
          <div>
            {['Cover Page', ...fixedSections, ...customSections].map((section) => (
              <div
                key={section}
                onClick={() => setActiveSection(section)}
                className={`custom-reports-section-item ${activeSection === section ? 'active' : ''}`}
              >
                {section}
              </div>
            ))}
          </div>
          <div style={{ marginTop: '1rem' }}>
            <button onClick={() => setCustomSections([...customSections, `Custom ${customSections.length + 1}`])} className="custom-reports-button">
              ➕ Add Section
            </button>
            <button onClick={() => generateFullReportPdf({ projectName: selectedProject?.name || '', coverData, sectionData, clientName: coverData.clientName, font: pdfFont, logoUrl: companyLogoUrl, footerHeightPt, customSections })} className="print-button">
              Print Report
            </button>
          </div>
          <div style={{ marginTop: 'auto', paddingBottom: '1rem' }}>
            <button onClick={() => navigate('/projects')} className="custom-reports-back-button">
              ← Back to Projects
            </button>
          </div>
        </div>

        <div className="custom-reports-content">
          {selectedProject ? (
            viewMode === 'edit' ? (
              activeSection === 'Cover Page' ? (
                <div className="custom-reports-editor-pane">
                  <label>Project Name:</label>
                  <input
                    value={selectedProject?.name || ''}
                    disabled
                    style={{ width: '100%', marginBottom: '1rem', backgroundColor: '#eee' }}
                  />
                  <label>Report Title:</label>
                  <input
                    value={coverData.projectDescription || ''}
                    onChange={(e) => updateCoverField('projectDescription', e.target.value)}
                    style={{ width: '100%', marginBottom: '1rem' }}
                  />
                  <label>Client Name:</label>
                  <input
                    value={coverData.clientName || ''}
                    onChange={(e) => updateCoverField('clientName', e.target.value)}
                    style={{ width: '100%', marginBottom: '1rem' }}
                  />
                  <label>Client Address:</label>
                  <textarea
                    value={coverData.clientAddress || ''}
                    rows={5}
                    onChange={(e) => updateCoverField('clientAddress', e.target.value)}
                    style={{ width: '100%', marginBottom: '1rem' }}
                  />
                  <label htmlFor="coverUpload">Upload Cover Image:</label>
                  <input id="coverUpload" type="file" onChange={handleImageUpload} />
                  {uploading && <p>Uploading...</p>}
                </div>
              ) : (
                <div className="custom-reports-editor-pane">
                  <RichTextEditor
                    ref={editorRef}
                    value={sectionData[activeSection] || ''}
                    onChange={(value) => {
                      setSectionData(prev => ({ ...prev, [activeSection]: value }));
                      if (selectedProject) {
                        clearTimeout(debounceTimers.current[activeSection]);
                        debounceTimers.current[activeSection] = setTimeout(() => {
                          setDoc(
                            doc(db, 'projects', selectedProject.id, 'reportSections', activeSection),
                            { content: value },
                            { merge: true }
                          );
                        }, 500);
                      }
                    }}
                  />
                </div>
              )
            ) : (
              <div className="custom-reports-preview-pane">
                <PaginatedPreview
                  sectionName={activeSection}
                  content={activeSection === 'Cover Page' ? coverData.clientAddress : sectionData[activeSection] || ''}
                  fontFamily={pdfFont}
                  clientName={coverData.clientName}
                  reportTitle={coverData.projectDescription}
                  logoUrl={companyLogoUrl}
                  footerHeightPt={footerHeightPt}
                  coverImageUrl={coverData.coverImageUrl}
                  isCover={activeSection === 'Cover Page'}
                  projectName={selectedProject?.name}
                />
              </div>
            )
          ) : (
            <p><strong>Please select a project</strong></p>
          )}
        </div>
      </div>

      {showImagePicker && selectedProject && (
        <ImageSelectorModal
          projectId={selectedProject.id}
          onClose={() => setShowImagePicker(false)}
        />
      )}
    </>
  );
}

export default CustomReportsPage;
